import { heapTrackingForMainPageEmailProfile } from "app/helpers/heapTracking";
import { ContactPerformerPopUp } from "component-library";
import { SubmitFormEndpoint } from "component-library/src/components/Organisms/ContactPerformerPopUp/PerformerContact";
import { UserClaimTypes, UserManager } from "core";
import { dataTestIds } from "data-testids";
import { useTranslation } from "i18n";
import { SPOTLIGHT_DEFAULT_AGENCY_REF } from "pages/AgenciesPage/components/AgenciesForm/AgenciesForm";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import AgentContactBlock from "pages/Profile/components/RepsSection/components/AgentContactBlock";
import SelfRepContactBlock from "pages/Profile/components/RepsSection/components/SelfRepContactBlock";
import useGetReps from "pages/Profile/hooks/useGetReps";
import { getAgentAddress, getAgentPhones } from "pages/Profile/utils/common";
import {
  checkIsAgent,
  checkIsCD,
  getUserAttribute,
  UserAttributes,
} from "shared-auth";
import { PROFILE_CONTACT_FULL_PATH } from "shared/constants";
import { useToggle } from "usehooks-ts";

interface Props {
  isEdit?: boolean;
}

const RepsSection = ({ isEdit }: Props) => {
  const { t } = useTranslation();
  const [isContactModalOpen, toggleContactModal] = useToggle();
  const {
    agents = [],
    schools = [],
    isLookingForRepresentation,
    isLoading,
  } = useGetReps();

  const isAgent = checkIsAgent();
  const isCd = checkIsCD();

  const seekingRepresentation = isAgent && isLookingForRepresentation;

  const agentLists = [
    ...agents,
    ...schools.map((school) => ({
      ...school,
      name: t("performer:profile.training.title.named", { name: school.name }),
    })),
  ];

  const handleOpenContactPerformerPopUp = () => {
    toggleContactModal();
    heapTrackingForMainPageEmailProfile(true);
  };

  return (
    <ProfileCard
      className="grid grid-cols-1 gap-4 text-s"
      isEdit={!isAgent && isEdit}
      editToPath={PROFILE_CONTACT_FULL_PATH}
      isLoading={isLoading}
      data-testid={
        dataTestIds.profile["Profile.ProfileCard.representationSection"]
      }
    >
      {agentLists.map((agent) => {
        const address = getAgentAddress(agent);

        const phoneNumbers = getAgentPhones(agent);

        return (
          <div
            key={agent.refNumber}
            className="border-b border-white/10 pb-1.5 last:border-b-0"
            data-testid={`${dataTestIds.profile["Profile.Agency.agencyDetails"]}-${agent.name}`}
          >
            {agent.refNumber === SPOTLIGHT_DEFAULT_AGENCY_REF ? (
              <SelfRepContactBlock
                onOpenContactPerformerPopUp={handleOpenContactPerformerPopUp}
                seekingRepresentation={seekingRepresentation}
              />
            ) : (
              <AgentContactBlock
                agent={agent}
                address={address}
                phoneNumbers={phoneNumbers}
              />
            )}
          </div>
        );
      })}

      <ContactPerformerPopUp
        isOpen={isContactModalOpen}
        closeForm={toggleContactModal}
        performerName={UserManager.getClaimWithDefault(
          UserClaimTypes.profileFullName
        )}
        senderName={getUserAttribute(UserAttributes.FullName)}
        senderEmail={getUserAttribute(UserAttributes.Email)}
        performerId={UserManager.userId}
        canAttachFiles={isCd}
        emailSubjectLine={
          isAgent
            ? t("performer:profile.representation.fallbackEmailSubject")
            : undefined
        }
        submitformEndpoint={
          isAgent
            ? SubmitFormEndpoint.LookingForRep
            : SubmitFormEndpoint.SelfRep
        }
      />
    </ProfileCard>
  );
};

export default RepsSection;
